import { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import { PromoRole, getRolesForUser } from '~/services/PromoService';

import ApiError from '~/types/ApiError';

type UseUserPromoRolesProps = {
  userId: string;
  enabled?: boolean;
};

type UseUserPromoRolesResult = {
  isLoading: boolean;
  roles: Array<PromoRole>;
  error: Error | null;
  refetch: () => Promise<void>;
  hasRole: (roleName: string) => boolean;
};

export function useUserPromoRoles({ userId, enabled = true }: UseUserPromoRolesProps): UseUserPromoRolesResult {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [roles, setRoles] = useState<Array<PromoRole>>([]);
  const [error, setError] = useState<Error | null>(null);

  const fetchUserRoles = useCallback(async () => {
    if (!enabled || !userId) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await getRolesForUser(userId);

      if (response.status === 200) {
        setRoles(response.result.roles);
      } else {
        throw new Error(`Failed to fetch user promo roles: ${response.status}`);
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch user promo roles');
      setError(error);

      const isAuthError = err instanceof ApiError && (err.status === 401 || err.status === 403);
      if (!isAuthError) {
        enqueueSnackbar(`Error fetching user promo roles: ${error.message}`, { variant: 'error' });
      }
    } finally {
      setIsLoading(false);
    }
  }, [userId, enabled, enqueueSnackbar]);

  const hasRole = useCallback(
    (roleName: string): boolean => {
      return roles.some((role) => role.roleName === roleName && role.isActive);
    },
    [roles],
  );

  useEffect(() => {
    fetchUserRoles();
  }, [fetchUserRoles]);

  return {
    isLoading,
    roles,
    error,
    refetch: fetchUserRoles,
    hasRole,
  };
}
